import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalizationService } from '@abp/ng.core';
import { Translation } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { all as primeLocales } from 'primelocale/js/all.js';

const PRIME_LOCALES = primeLocales as unknown as Record<string, Translation>;
const FALLBACK_LANGUAGE = 'en';

@Injectable({ providedIn: 'root' })
export class PrimeLocaleService {
  private readonly localization = inject(LocalizationService);
  private readonly primeNg = inject(PrimeNG);
  private readonly destroyRef = inject(DestroyRef);

  initialize(): void {
    this.applyForLanguage(this.localization.currentLang);

    this.localization.languageChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(language => this.applyForLanguage(language));
  }

  private applyForLanguage(language: string | undefined): void {
    const normalized = this.normalizeLanguage(language);
    const translation = this.findTranslation(normalized);

    this.primeNg.setTranslation({ ...translation });
  }

  private normalizeLanguage(language: string | undefined): string {
    return (language || FALLBACK_LANGUAGE).trim();
  }

  private findTranslation(language: string): Translation {
    const exact = PRIME_LOCALES[language];
    if (exact) {
      return exact;
    }

    const underscore = PRIME_LOCALES[language.replace(/-/g, '_')];
    if (underscore) {
      return underscore;
    }

    const hyphen = PRIME_LOCALES[language.replace(/_/g, '-')];
    if (hyphen) {
      return hyphen;
    }

    const baseLanguage = language.split(/[-_]/)[0]?.toLowerCase();
    if (baseLanguage && PRIME_LOCALES[baseLanguage]) {
      return PRIME_LOCALES[baseLanguage];
    }

    return PRIME_LOCALES[FALLBACK_LANGUAGE];
  }
}
